import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { useParams } from 'react-router-dom';
import { Button, FormHelperText, Grid, Group, Loader, Token, Typography, Wrapper } from '@screentone/core';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

import { WidePageWrapper } from 'components';
import { Article } from 'components/datamodel/content/Article/Article';
import EmbargoWarning from 'components/embargo-warning/EmbargoWarning';
import PageLock from 'components/page-lock/PageLock';
import { PageRootContainer } from 'components/page-root-container/PageRootContainer';
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
import { EntityType } from 'contexts/datamodel/DataModelContext';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { DRAGGABLE_PREFIXES } from 'contexts/drag-and-drop/dragUtils';
import { useDragAndDrop } from 'contexts/drag-and-drop/useDragAndDrop';
import { usePageLock } from 'contexts/page-lock/usePageLock';
import {
  ArticleItem,
  AvailableFeatureFlags,
  CollectionDto,
  ListContainer,
  PublicationSettingSearchableContentType,
  QueryItem,
  useGetLiveContentByAllessehIdQuery,
  usePagesDtoForCollectionIdQuery
} from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AllessehContent, AllessehQuerySortKeyTypes } from 'hooks/useAllessehContentQuery';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { setJsonQueryStr } from 'utils/collection';
import { DEFAULT_CONTENT_SEARCH_FILTERS } from 'utils/constants';
import { determineDateDisplay, formatInTimeZone } from 'utils/dates';
import styles from './CollectionEdit.module.scss';
import { CollectionCurateSection } from './components/collection-curate-section/CollectionCurateSection';
import { CollectionInfoBoxesSection } from './components/collection-info-boxes-section/CollectionInfoBoxesSection';
import { useCollectionPublish } from './hooks/useCollectionPublish';
import { SearchParams } from '../../components/search-content/SearchContent';

export const CollectionEdit = () => {
  const { idCreatedUtc = '' } = useParams();

  const { root, metadata, getAllArticleIds, fromAllessehContent, renderEntity, modifyEntity, getSortedEmbargoContent } =
    useDataModelContext<ListContainer>();
  const { generateDraggableId } = useDragAndDrop();
  const {
    hasCollectionChanged,
    notificationText,
    handleSave,
    isUpdating,
    handleCopy,
    isCopying,
    handleArchive,
    isArchiving,
    handleOffPlatformConvert,
    isConvertingToOffPlatform,
    waitingForAllesseh
  } = useCollectionPublish();
  const { renderActions } = useContextMenuActions();

  const currentProperty = useConvertedProperty();
  const { data: publicationSettingsResp } = usePublicationSettings();
  const [liveVersion, setLiveVersion] = useState<CollectionDto | undefined>(undefined);
  const [hasRecentlyPublished, setHasRecentlyPublished] = useState(false);

  const { data: liveContentData, refetch: refetchLiveContent } = useGetLiveContentByAllessehIdQuery(
    { allessehId: metadata.allessehId, publicationKey: currentProperty ?? '' },
    { enabled: !!metadata.allessehId && !!currentProperty }
  );

  useEffect(() => {
    if (liveContentData?.liveContentByAllessehId[0]?.body) {
      setLiveVersion(liveContentData.liveContentByAllessehId[0].body as CollectionDto);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveContentData?.liveContentByAllessehId]);

  useEffect(() => {
    if (hasRecentlyPublished) {
      const timer = setTimeout(() => setHasRecentlyPublished(false), 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [hasRecentlyPublished]);

  const { state, lockPageFragment, releasePageFragment, loading, refetcher } = usePageLock();
  const collectionPublishing = state.publishing;
  const autolockAttemptedRef = useRef(false);
  const [lockIsReady, setLockIsReady] = useState(false);
  const isCollectionLockingEnabled = useFeatureFlag(AvailableFeatureFlags.CollectionLockingEnabled, false);

  const handleLock = useCallback(
    async (withForce: boolean) => {
      const result = await lockPageFragment('publishing', withForce);
      refetcher?.();
      return result;
    },
    [lockPageFragment, refetcher]
  );

  useEffect(() => {
    if (!loading && !autolockAttemptedRef.current && isCollectionLockingEnabled) {
      autolockAttemptedRef.current = true;

      // Attempt to acquire lock on mount
      lockPageFragment('publishing')
        .catch(console.error)
        .finally(() => {
          setLockIsReady(true);
        });
    }
  }, [loading, lockPageFragment, isCollectionLockingEnabled]);

  // search
  const articleIds = getAllArticleIds();
  const defaultFilterState = useMemo(
    () => ({
      ...DEFAULT_CONTENT_SEARCH_FILTERS,
      contentTypes: publicationSettingsResp?.publicationSetting.defaultContentTypes ?? [
        PublicationSettingSearchableContentType.Article
      ],
      excludedContentIds: articleIds
    }),
    [articleIds, publicationSettingsResp?.publicationSetting.defaultContentTypes]
  );
  const [searchParams, setSearchParams] = useState<SearchParams>({
    search: '',
    filters: defaultFilterState,
    sort: AllessehQuerySortKeyTypes.LiveDate
  });

  useEffect(() => {
    const newSearchParams = cloneDeep(searchParams);
    if (publicationSettingsResp) {
      newSearchParams.filters.contentTypes = publicationSettingsResp.publicationSetting.defaultContentTypes;
      setSearchParams(newSearchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicationSettingsResp]);

  useEffect(() => {
    const newSearchParams = cloneDeep(searchParams);
    if (
      newSearchParams.filters.contentTypes.length > 0 && // ensure contentTypes list has loaded and has at least 1 item
      !isEqual(newSearchParams.filters.excludedContentIds, articleIds)
    ) {
      newSearchParams.filters.excludedContentIds = articleIds;
      setSearchParams(newSearchParams);
    }
  }, [articleIds, searchParams]);

  const handleSearchParamsChange = (newSearchParams: SearchParams) => {
    setSearchParams(newSearchParams);
  };

  const {
    data: pagesForCollectionData,
    isLoading: isLoad,
    fetchStatus
  } = usePagesDtoForCollectionIdQuery(
    { publicationKey: currentProperty!, collectionId: idCreatedUtc.replace(/_\d{13}$/, '') },
    { enabled: !!currentProperty && !!idCreatedUtc }
  );
  const pagesAreLoading = isLoad && fetchStatus !== 'idle';

  const queryItemIndex = root.collection.findIndex((item) => item.type === EntityType.Query);
  const queryItem = queryItemIndex !== -1 ? (root.collection[queryItemIndex] as QueryItem) : undefined;
  const queryBody = queryItem?.attributes.query;
  let allessehJsonQuery: string | null = null;
  if (queryBody) {
    allessehJsonQuery = JSON.stringify(queryBody);
  }

  const isArchived = 'isArchived' in metadata && metadata.isArchived;
  const draftSection = renderEntity(root, { hierarchyId: '', isReadOnly: collectionPublishing.isReadOnly });

  const handleJsonQueryStr = (newQueryStr: string) => {
    setJsonQueryStr(newQueryStr, queryItemIndex, modifyEntity);
  };

  const handleSaveClick = async () => {
    const saved = await handleSave();
    if (saved) {
      await refetchLiveContent();
      setHasRecentlyPublished(true);
    }
  };

  const latestEmbargoContent = getSortedEmbargoContent('desc').at(0);

  let embargoDate: string;
  let embargoTime: string;
  let formattedDate: string;
  let formattedTime: string;
  let showDate: boolean;
  let showTime: boolean;

  return (
    <PageRootContainer noBottomMargin>
      <WidePageWrapper data-testid="collection-edit-container" padding={{ all: 'md' }}>
        <>
          <Wrapper data-testid="collection-edit-header" padding={{ top: 'md', bottom: 'md' }}>
            <Group align="space-between" fullWidth gap="sm">
              <Group gap="sm">
                <Typography data-testid="collection-edit-title" variant="header2" margin={{ bottom: 'none' }}>
                  {metadata.name}
                </Typography>
                {collectionPublishing.isReadOnly && lockIsReady && (
                  <Token color="gray" data-testid="read-only-label">
                    Read Only
                  </Token>
                )}
                {pagesForCollectionData && pagesForCollectionData.pagesDTOForCollectionId.length > 0 && (
                  <Token data-testid="collection-edit-status" color="emerald">
                    In Use
                  </Token>
                )}
                {notificationText && (
                  <Typography data-testid="collection-edit-update" color="asphalt">
                    {notificationText}
                  </Typography>
                )}
                <EmbargoWarning
                  embargoInfo={latestEmbargoContent}
                  preferredTimezone={publicationSettingsResp?.publicationSetting.preferredTimezone}
                  dataTestId="collection-embargo-warning"
                />
              </Group>
              <Group>
                {lockIsReady && isCollectionLockingEnabled && (
                  <PageLock
                    publishingState={collectionPublishing}
                    release={(withForce) => releasePageFragment('publishing', withForce)}
                    lock={handleLock}
                    loading={loading}
                    draftEnabled={false}
                    lockType="Collection"
                  />
                )}
                {!collectionPublishing.isReadOnly && (
                  <Button
                    primary
                    onClick={handleSaveClick}
                    disabled={isArchived || isUpdating || !hasCollectionChanged}
                    data-testid="collection-save-button"
                  >
                    {isUpdating && <Loader size="md" margin={{ right: 'md' }} />}
                    Save
                  </Button>
                )}
                {isArchived && !isUpdating && hasCollectionChanged && (
                  <FormHelperText>You cannot update a collection when archived.</FormHelperText>
                )}
              </Group>
            </Group>
          </Wrapper>
          <Wrapper data-testid="collection-edit-details" margin={{ top: 'sm' }}>
            <Grid>
              <Grid.Row xsCols={12}>
                <Grid.Col xs={3}>
                  <CollectionInfoBoxesSection
                    pagesForCollection={pagesForCollectionData}
                    pagesAreLoading={pagesAreLoading}
                    hasCollectionChanged={hasCollectionChanged}
                    isUpdating={isUpdating}
                    handleCopy={handleCopy}
                    isCopying={isCopying}
                    handleArchive={handleArchive}
                    isArchiving={isArchiving}
                    handleOffPlatformConvert={handleOffPlatformConvert}
                    isConvertingToOffPlatform={isConvertingToOffPlatform}
                    waitingForAllesseh={waitingForAllesseh}
                    isReadOnly={collectionPublishing.isReadOnly}
                  />
                </Grid.Col>
                <Grid.Col xs={4}>{draftSection}</Grid.Col>
                <Grid.Col xs={5}>
                  <CollectionCurateSection
                    isReadOnly={collectionPublishing.isReadOnly}
                    searchProps={{
                      defaultFilterState,
                      searchParams,
                      onSearchParamsChange: handleSearchParamsChange,
                      resultListClassName: styles.curateScrollable,
                      renderContentCard: (content: AllessehContent, index: number, embargo?: boolean) => (
                        <Draggable
                          draggableId={generateDraggableId(DRAGGABLE_PREFIXES.COLLECTION_ITEM, { content })}
                          index={index}
                        >
                          {(draggableProvided) => {
                            const articleItem = fromAllessehContent(content) as ArticleItem;
                            if (embargo) {
                              const dateObject = new Date(content.data.attributes.embargo_datetime_utc as string);
                              formattedDate = formatInTimeZone(
                                dateObject,
                                publicationSettingsResp?.publicationSetting.preferredTimezone,
                                'LLL d, yyyy'
                              );
                              formattedTime = formatInTimeZone(
                                dateObject,
                                publicationSettingsResp?.publicationSetting.preferredTimezone,
                                'h:mm aaaa',
                                { showTimezoneAbbreviation: true }
                              );
                              ({ showTime, showDate, embargoTime, embargoDate } = determineDateDisplay(
                                embargoDate,
                                formattedDate,
                                embargoTime,
                                formattedTime
                              ));
                            }
                            return (
                              <>
                                {embargo && (index < 1 || showDate) && (
                                  <Typography
                                    margin={{ top: 'sm', bottom: 'md' }}
                                    data-testid="page-content-search-date-label"
                                  >
                                    {formattedDate}
                                  </Typography>
                                )}
                                {embargo && (index < 1 || showTime) && (
                                  <Typography
                                    variant="label3"
                                    margin={{ bottom: 'sm' }}
                                    data-testid="page-content-search-time-label"
                                  >
                                    {formattedTime}
                                  </Typography>
                                )}
                                <div
                                  data-testid="collection-edit-card-content"
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  {...draggableProvided.dragHandleProps}
                                >
                                  <Article
                                    data={articleItem}
                                    renderActions={() =>
                                      renderActions(MenuActionsKeys.ArticleSearch, {
                                        entity: articleItem,
                                        hierarchyId: `0-${root.collection.length - 1}`,
                                        extraProperties: {
                                          allessehContent: content,
                                          jsonQueryStr: allessehJsonQuery ?? undefined,
                                          setJsonQueryStr: handleJsonQueryStr
                                        }
                                      })
                                    }
                                  />
                                </div>
                              </>
                            );
                          }}
                        </Draggable>
                      )
                    }}
                    queryProps={{
                      allessehJsonQuery,
                      queryItemIndex,
                      totalItemsLength: root.collection.length
                    }}
                    liveVersion={liveVersion}
                    hasRecentlyPublished={hasRecentlyPublished}
                  />
                </Grid.Col>
              </Grid.Row>
            </Grid>
          </Wrapper>
        </>
      </WidePageWrapper>
    </PageRootContainer>
  );
};
